import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { skillKeys } from "@/lib/query";
import type { Category, ExampleBlock } from "@/lib/skills/types";
import { uploadArtifact } from "@/lib/skills/upload";

export interface EditMetadataInput {
  title: string;
  desc: string;
  categories: Category[];
  whatIs: string;
  whyImportant: string;
  notable: string;
  install: string;
  notes?: string;
  examples: ExampleBlock[];
  installSteps: string[];
}

async function postJson(url: string, body?: unknown, method = "POST") {
  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(typeof j.error === "string" ? j.error : "Request failed");
  }
  return res.json();
}

export function useDownloadSkill(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => postJson(`/api/skills/${slug}/download`),
    onSuccess: (json: { data: { title: string; version: string; url: string } }) => {
      qc.invalidateQueries({ queryKey: skillKeys.detail(slug) });
      qc.invalidateQueries({ queryKey: skillKeys.all });
      // Navigate the browser to the short-lived presigned S3 URL so the artifact
      // downloads. A hidden anchor avoids replacing the current SPA view.
      const a = document.createElement("a");
      a.href = json.data.url;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Downloaded ${json.data.title} v${json.data.version}`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Download failed"),
  });
}

export function usePostReview(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { rating: number; text: string }) =>
      postJson(`/api/skills/${slug}/reviews`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: skillKeys.detail(slug) });
      qc.invalidateQueries({ queryKey: skillKeys.all });
      toast.success("Review posted — thanks for the signal");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not post review"),
  });
}

export function useTogglePublish(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (published: boolean) =>
      postJson(`/api/skills/${slug}/publish`, { published }, "PATCH"),
    onSuccess: (json: { data: { title: string; published: boolean } }) => {
      qc.invalidateQueries({ queryKey: skillKeys.detail(slug) });
      qc.invalidateQueries({ queryKey: skillKeys.all });
      toast.success(
        json.data.published ? `Published ${json.data.title}` : `Unpublished ${json.data.title}`,
      );
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Publish toggle failed"),
  });
}

export function useUpdateFinding(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { findingId: string; status: "acknowledged" | "dismissed" }) =>
      postJson(
        `/api/skills/${slug}/findings/${input.findingId}`,
        { status: input.status },
        "PATCH",
      ),
    onSuccess: (_json, input) => {
      qc.invalidateQueries({ queryKey: skillKeys.detail(slug) });
      toast.success(input.status === "acknowledged" ? "Finding acknowledged" : "Finding dismissed");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not update finding"),
  });
}

export function useUpdateSkill(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EditMetadataInput) => postJson(`/api/skills/${slug}`, input, "PATCH"),
    onSuccess: (json: { data: { title: string } }) => {
      qc.invalidateQueries({ queryKey: skillKeys.detail(slug) });
      qc.invalidateQueries({ queryKey: skillKeys.all });
      toast.success(`Updated ${json.data.title}`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not save changes"),
  });
}

export function useSubmitNewVersion(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; version: string; notes?: string }) => {
      const artifactKey = await uploadArtifact(input.file);
      return postJson(`/api/skills/${slug}/versions`, {
        version: input.version,
        notes: input.notes,
        artifactKey,
      });
    },
    onSuccess: (json: { data: { version: string } }) => {
      qc.invalidateQueries({ queryKey: skillKeys.detail(slug) });
      qc.invalidateQueries({ queryKey: skillKeys.all });
      qc.invalidateQueries({ queryKey: skillKeys.mine });
      toast.success(`Published v${json.data.version}`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not submit version"),
  });
}
